import React, { useState, useEffect } from 'react';
import {
  TruckIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import ModalEditarVehiculo from '../../../componentes/modales/ModalEditarVehiculo';
import ModalFiltrosFlota from '../../../componentes/modales/ModalFiltrosFlota';
import ModalDocumentosVehiculo from '../../../componentes/modales/ModalDocumentosVehiculo';
import ModalConfirmacion from '../../../componentes/modales/ModalConfirmacion';
import ModalDetallesVehiculo from '../../../componentes/modales/ModalDetallesVehiculo';
import ModalMapaVehiculo from '../../../componentes/modales/ModalMapaVehiculo';
import { vehiculosService, Vehiculo } from '../../../services/vehiculosService';
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion';

interface FiltrosFlota {
  busqueda: string;
  tipo: string[];
  estado: string[];
  conductor: string;
  fechaInicio: string;
  fechaFin: string;
  capacidadMin: number;
  capacidadMax: number;
  consumoMin: number;
  consumoMax: number;
  añoMin: number;
  añoMax: number;
  marca: string[];
  mantenimientoProximo: boolean;
  sinConductor: boolean;
}

const PaginaListaFlota: React.FC = () => {
  const { usuario } = useAuth();
  const idOrganizacion = usuario?.id_organizacion;

  // Estados principales
  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [vehiculosFiltrados, setVehiculosFiltrados] = useState<Vehiculo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados de paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const vehiculosPorPagina = 10;

  // Estados de filtros
  const [filtros, setFiltros] = useState<FiltrosFlota>({
    busqueda: '',
    tipo: [],
    estado: [],
    conductor: '',
    fechaInicio: '',
    fechaFin: '',
    capacidadMin: 0,
    capacidadMax: 50000,
    consumoMin: 0,
    consumoMax: 50,
    añoMin: 2000,
    añoMax: new Date().getFullYear(),
    marca: [],
    mantenimientoProximo: false,
    sinConductor: false
  });

  // Estados de modales
  const [modalEditarAbierto, setModalEditarAbierto] = useState(false);
  const [modalDetallesAbierto, setModalDetallesAbierto] = useState(false);
  const [modalMapaAbierto, setModalMapaAbierto] = useState(false);
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);
  const [modalConfirmacionAbierto, setModalConfirmacionAbierto] = useState(false);
  const [modalDocumentosAbierto, setModalDocumentosAbierto] = useState(false);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState<any | null>(null);
  const [accionConfirmacion, setAccionConfirmacion] = useState<{
    tipo: 'eliminar' | 'mantenimiento' | 'fuera_servicio';
    vehiculo: any | null;
  }>({ tipo: 'eliminar', vehiculo: null });

  // Efectos
  useEffect(() => {
    if (typeof idOrganizacion === 'number') {
      cargarVehiculos(idOrganizacion);
      return;
    }

    setVehiculos([]);
    setVehiculosFiltrados([]);
    setError('No se encontró una organización activa para consultar la flota.');
    setCargando(false);
  }, [idOrganizacion]);

  useEffect(() => {
    aplicarFiltros();
  }, [vehiculos, filtros]);

  // Funciones de carga de datos
  const cargarVehiculos = async (tenantId: number) => {
    try {
      setCargando(true);
      const data = await vehiculosService.obtenerVehiculos(undefined, tenantId);
      setVehiculos(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los vehículos');
      console.error('Error:', err);
    } finally {
      setCargando(false);
    }
  };

  // Funciones de filtrado
  const aplicarFiltros = () => {
    if (!vehiculos) return [];
    let vehiculosFiltrados = [...vehiculos];

    // Filtro de búsqueda general
    if (filtros.busqueda) {
      const busqueda = filtros.busqueda.toLowerCase();
      vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
        vehiculo.placa.toLowerCase().includes(busqueda) ||
        vehiculo.marca.toLowerCase().includes(busqueda) ||
        vehiculo.modelo.toLowerCase().includes(busqueda)
      );
    }

    // Filtro por tipo
    if (filtros.tipo && filtros.tipo.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
        filtros.tipo.includes(vehiculo.tipo)
      );
    }

    // Filtro por estado
    if (filtros.estado && filtros.estado.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
        filtros.estado.includes(vehiculo.estado)
      );
    }

    // Filtro por marca
    if (filtros.marca && filtros.marca.length > 0) {
      vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
        filtros.marca.includes(vehiculo.marca)
      );
    }

    // Filtro por conductor (deshabilitado temporalmente hasta que se traiga de la API)
    if (filtros.conductor) {
      // const conductor = filtros.conductor.toLowerCase();
      // vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
      //   vehiculo.conductor?.nombre.toLowerCase().includes(conductor)
      // );
    }

    // Filtro por capacidad
    vehiculosFiltrados = vehiculosFiltrados.filter(vehiculo =>
      vehiculo.capacidadCarga >= filtros.capacidadMin &&
      vehiculo.capacidadCarga <= filtros.capacidadMax
    );

    setVehiculosFiltrados(vehiculosFiltrados);
    setPaginaActual(1);
  };

  // Funciones de manejo de modales
  const abrirModalCrear = () => {
    setVehiculoSeleccionado(null);
    setModalEditarAbierto(true);
  };

  const abrirModalDetalles = (vehiculo: any) => {
    setVehiculoSeleccionado(vehiculo);
    setModalDetallesAbierto(true);
  };

  const cerrarModalDetalles = () => {
    setModalDetallesAbierto(false);
    setVehiculoSeleccionado(null);
  };

  const abrirModalMapa = (vehiculo: any) => {
    setVehiculoSeleccionado(vehiculo);
    setModalMapaAbierto(true);
  };

  const cerrarModalMapa = () => {
    setModalMapaAbierto(false);
    setVehiculoSeleccionado(null);
  };

  const abrirModalEditar = (vehiculo: any) => {
    setVehiculoSeleccionado(vehiculo);
    setModalEditarAbierto(true);
  };

  const cerrarModalEditar = () => {
    setModalEditarAbierto(false);
    setVehiculoSeleccionado(null);
  };

  const abrirModalFiltros = () => {
    setModalFiltrosAbierto(true);
  };

  const cerrarModalFiltros = () => {
    setModalFiltrosAbierto(false);
  };

  const abrirModalDocumentos = (vehiculo: any) => {
    setVehiculoSeleccionado(vehiculo);
    setModalDocumentosAbierto(true);
  };

  const cerrarModalDocumentos = () => {
    setModalDocumentosAbierto(false);
    setVehiculoSeleccionado(null);
  };

  const abrirModalConfirmacion = (tipo: 'eliminar' | 'mantenimiento' | 'fuera_servicio', vehiculo: any) => {
    setAccionConfirmacion({ tipo, vehiculo });
    setModalConfirmacionAbierto(true);
  };

  const cerrarModalConfirmacion = () => {
    setModalConfirmacionAbierto(false);
    setAccionConfirmacion({ tipo: 'eliminar', vehiculo: null });
  };

  const confirmarAccion = async () => {
    if (!accionConfirmacion.vehiculo) return;

    try {
      if (accionConfirmacion.tipo === 'eliminar') {
        await vehiculosService.eliminarVehiculo(accionConfirmacion.vehiculo.id);
        setVehiculos(prev => prev.filter(v => v.id !== accionConfirmacion.vehiculo!.id));
      } else {
        await vehiculosService.cambiarEstado(accionConfirmacion.vehiculo.id, accionConfirmacion.tipo);
        setVehiculos(prev => prev.map(v => 
          v.id === accionConfirmacion.vehiculo!.id 
            ? { ...v, estado: accionConfirmacion.tipo as any }
            : v
        ));
      }
      
      cerrarModalConfirmacion();
    } catch (error) {
      console.error('Error al realizar la acción:', error);
    }
  };

  // Funciones de utilidad
  const obtenerIconoTipo = (tipo: string) => {
    switch (tipo) {
      case 'camion':
        return <TruckIcon className="h-5 w-5" />;
      case 'camioneta':
        return <TruckIcon className="h-5 w-5" />;
      case 'furgon':
        return <TruckIcon className="h-5 w-5" />;
      case 'motocicleta':
        return <TruckIcon className="h-5 w-5" />;
      default:
        return <TruckIcon className="h-5 w-5" />;
    }
  };

  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'disponible':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'en_ruta':
        return <ArrowPathIcon className="h-5 w-5 text-blue-500" />;
      case 'mantenimiento':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'fuera_servicio':
        return <ClockIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  

  // Cálculos de paginación
  const indiceInicio = (paginaActual - 1) * vehiculosPorPagina;
  const indiceFin = indiceInicio + vehiculosPorPagina;
  const vehiculosPaginados = vehiculosFiltrados.slice(indiceInicio, indiceFin);
  const totalPaginas = Math.ceil(vehiculosFiltrados.length / vehiculosPorPagina);

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando flota...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-lg rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-center text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <TruckIcon className="h-8 w-8 mr-3 text-blue-600" />
                Gestión de Flota
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Administra y monitorea tu flota de vehículos
              </p>
            </div>
            <button
              onClick={abrirModalCrear}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Agregar Vehículo
            </button>
          </div>
        </div>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Disponibles</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {vehiculos ? vehiculos.filter(v => v.estado === 'disponible').length : 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <ArrowPathIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En Ruta</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {vehiculos ? vehiculos.filter(v => v.estado === 'en_ruta').length : 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mantenimiento</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {vehiculos ? vehiculos.filter(v => v.estado === 'mantenimiento').length : 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <TruckIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{vehiculos ? vehiculos.length : 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <label htmlFor="lista-flota-busqueda" className="sr-only">Buscar vehículo</label>
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                id="lista-flota-busqueda"
                name="filtros.busqueda"
                type="text"
                placeholder="Buscar por placa, marca, modelo..."
                value={filtros.busqueda}
                onChange={(e) => setFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <label htmlFor="lista-flota-estado" className="sr-only">Estado</label>
            <select
              id="lista-flota-estado"
              name="filtros.estado"
              value={filtros.estado.join(',')}
              onChange={(e) => setFiltros(prev => ({ 
                ...prev, 
                estado: e.target.value ? e.target.value.split(',') : [] 
              }))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos los estados</option>
              <option value="disponible">Disponible</option>
              <option value="en_ruta">En Ruta</option>
              <option value="mantenimiento">Mantenimiento</option>
              <option value="fuera_servicio">Fuera de Servicio</option>
            </select>
            
            <label htmlFor="lista-flota-tipo" className="sr-only">Tipo</label>
            <select
              id="lista-flota-tipo"
              name="filtros.tipo"
              value={filtros.tipo.join(',')}
              onChange={(e) => setFiltros(prev => ({ 
                ...prev, 
                tipo: e.target.value ? e.target.value.split(',') : [] 
              }))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">Todos los tipos</option>
              <option value="camion">Camión</option>
              <option value="camioneta">Camioneta</option>
              <option value="furgon">Furgón</option>
              <option value="motocicleta">Motocicleta</option>
            </select>
            
            <button
              onClick={abrirModalFiltros}
              className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg flex items-center justify-center transition-colors"
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
              Filtros Avanzados
            </button>
          </div>
        </div>

        {/* Tabla de vehículos */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Vehículo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Conductor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ubicación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Capacidad
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Documentos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {vehiculosPaginados.map((vehiculo) => (
                  <tr key={vehiculo.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            {obtenerIconoTipo(vehiculo.tipo)}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {vehiculo.placa}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {vehiculo.marca} {vehiculo.modelo} ({vehiculo.año})
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Sin asignar</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {obtenerIconoEstado(vehiculo.estado)}
                        <span className="ml-2 text-sm text-gray-900 dark:text-white capitalize">
                          {vehiculo.estado.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 dark:text-gray-400">No disponible</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {vehiculo.capacidadCarga.toLocaleString()} kg
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => abrirModalDocumentos(vehiculo)}
                        className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <FolderIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm">0 docs</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => abrirModalDetalles(vehiculo)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Ver detalles"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => abrirModalEditar(vehiculo)}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          title="Editar"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => abrirModalMapa(vehiculo)}
                          className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
                          title="Ver en mapa"
                        >
                          <MapPinIcon className="h-4 w-4" />
                        </button>
                        {vehiculo.estado === 'disponible' && (
                          <button
                            onClick={() => abrirModalConfirmacion('mantenimiento', vehiculo)}
                            className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                            title="Enviar a mantenimiento"
                          >
                            <ExclamationTriangleIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => abrirModalConfirmacion('eliminar', vehiculo)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Eliminar"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                  disabled={paginaActual === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                  disabled={paginaActual === totalPaginas}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando{' '}
                    <span className="font-medium">{indiceInicio + 1}</span>
                    {' '}a{' '}
                    <span className="font-medium">
                      {Math.min(indiceFin, vehiculosFiltrados.length)}
                    </span>
                    {' '}de{' '}
                    <span className="font-medium">{vehiculosFiltrados.length}</span>
                    {' '}resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
                      disabled={paginaActual === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((numero) => (
                      <button
                        key={numero}
                        onClick={() => setPaginaActual(numero)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          numero === paginaActual
                            ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {numero}
                      </button>
                    ))}
                    <button
                      onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
                      disabled={paginaActual === totalPaginas}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modales */}
        <ModalEditarVehiculo
          isOpen={modalEditarAbierto}
          onClose={cerrarModalEditar}
          vehiculo={vehiculoSeleccionado}
          onSave={(vehiculoActualizado) => {
            if (!vehiculoSeleccionado) {
              setVehiculos(prev => [vehiculoActualizado, ...prev]);
            } else {
              setVehiculos(prev => prev.map(v =>
                v.id === vehiculoActualizado.id ? vehiculoActualizado : v
              ));
            }
            cerrarModalEditar();
          }}
        />

        <ModalDetallesVehiculo
          isOpen={modalDetallesAbierto}
          onClose={cerrarModalDetalles}
          vehiculo={vehiculoSeleccionado}
        />

        <ModalMapaVehiculo
          isOpen={modalMapaAbierto}
          onClose={cerrarModalMapa}
          vehiculo={vehiculoSeleccionado}
        />

        <ModalFiltrosFlota
          isOpen={modalFiltrosAbierto}
          onClose={cerrarModalFiltros}
          filtrosActuales={filtros}
          onAplicarFiltros={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            cerrarModalFiltros();
          }}
        />

        <ModalDocumentosVehiculo
          isOpen={modalDocumentosAbierto}
          onClose={cerrarModalDocumentos}
          vehiculo={vehiculoSeleccionado}
        />

        <ModalConfirmacion
          isOpen={modalConfirmacionAbierto}
          onClose={cerrarModalConfirmacion}
          titulo={
            accionConfirmacion.tipo === 'eliminar' 
              ? 'Eliminar Vehículo'
              : accionConfirmacion.tipo === 'mantenimiento'
              ? 'Enviar a Mantenimiento'
              : 'Marcar Fuera de Servicio'
          }
          mensaje={
            accionConfirmacion.tipo === 'eliminar'
              ? `¿Estás seguro de que deseas eliminar el vehículo ${accionConfirmacion.vehiculo?.placa}? Esta acción no se puede deshacer.`
              : accionConfirmacion.tipo === 'mantenimiento'
              ? `¿Deseas enviar el vehículo ${accionConfirmacion.vehiculo?.placa} a mantenimiento?`
              : `¿Deseas marcar el vehículo ${accionConfirmacion.vehiculo?.placa} como fuera de servicio?`
          }
          onConfirmar={confirmarAccion}
          tipoBoton={accionConfirmacion.tipo === 'eliminar' ? 'danger' : 'warning'}
        />
      </div>
    </div>
  );
};

export default PaginaListaFlota;
